import { onBeforeUnmount, onMounted, watch } from "vue";
import { useBreakpoints } from "./useBreakpoints";

/** Порог долгого нажатия. Ниже 400мс срабатывает на обычных тапах. */
const HOLD_MS = 450;
/** Сдвиг пальца, после которого жест считается прокруткой, а не удержанием. */
const MOVE_TOLERANCE_PX = 10;
/** Через столько подсказка уходит сама — закрывать её вручную не нужно. */
const AUTO_HIDE_MS = 4000;
const EDGE_GAP_PX = 8;

const HINT_CLASS = "touch-hint";

/**
 * Подсказки `title` на устройствах без курсора (CAB-489).
 *
 * В кабинете больше трёхсот элементов с атрибутом `title` — расшифровки
 * значков, статусов, сокращённых заголовков. На телефоне этот текст не
 * показывается вовсе: нативную подсказку браузер даёт только по наведению, а
 * навести пальцем нельзя. Половина смысла плотного интерфейса просто пропадала.
 *
 * Показываем подсказку по долгому нажатию — тому же жесту, которым системные
 * приложения раскрывают подписи значков. Обычный тап при этом продолжает
 * выполнять действие, и переписывать сами `title` нигде не нужно: обработчик
 * один на документ и подхватывает в том числе те, что появятся позже.
 */
export function useTouchHints(): void {
  const { isTouch } = useBreakpoints();

  let hint: HTMLElement | null = null;
  let holdTimer = 0;
  let hideTimer = 0;
  let startX = 0;
  let startY = 0;
  let pending: HTMLElement | null = null;
  let shown = false;

  function ensureHint(): HTMLElement {
    if (!hint) {
      hint = document.createElement("div");
      hint.className = HINT_CLASS;
      hint.setAttribute("role", "tooltip");
      document.body.appendChild(hint);
    }
    return hint;
  }

  /** Держим подсказку в пределах экрана: у краёв она иначе обрезается. */
  function place(element: HTMLElement, target: HTMLElement): void {
    const box = target.getBoundingClientRect();
    const size = element.getBoundingClientRect();

    const left = Math.min(
      Math.max(EDGE_GAP_PX, box.left + box.width / 2 - size.width / 2),
      window.innerWidth - size.width - EDGE_GAP_PX,
    );

    // Над целью, а если сверху не помещается — под ней: палец удерживает
    // элемент и закрывает всё, что нарисовано на нём самом.
    const above = box.top - size.height - EDGE_GAP_PX;
    const top = above >= EDGE_GAP_PX ? above : box.bottom + EDGE_GAP_PX;

    element.style.left = `${Math.round(left)}px`;
    element.style.top = `${Math.round(top)}px`;
  }

  function show(target: HTMLElement, text: string): void {
    const element = ensureHint();
    element.textContent = text;
    element.classList.add(`${HINT_CLASS}--visible`);
    place(element, target);
    shown = true;

    window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(hide, AUTO_HIDE_MS);
  }

  function hide(): void {
    shown = false;
    window.clearTimeout(hideTimer);
    hint?.classList.remove(`${HINT_CLASS}--visible`);
  }

  function cancelHold(): void {
    window.clearTimeout(holdTimer);
    holdTimer = 0;
    pending = null;
  }

  function onPointerDown(event: PointerEvent): void {
    if (shown) hide();
    if (event.pointerType === "mouse") return;

    const target = (event.target as Element | null)?.closest<HTMLElement>("[title]");
    const text = target?.getAttribute("title")?.trim();
    if (!target || !text) return;

    pending = target;
    startX = event.clientX;
    startY = event.clientY;

    holdTimer = window.setTimeout(() => {
      holdTimer = 0;
      if (pending) show(pending, text);
      pending = null;
    }, HOLD_MS);
  }

  function onPointerMove(event: PointerEvent): void {
    if (!holdTimer) return;
    if (Math.abs(event.clientX - startX) > MOVE_TOLERANCE_PX || Math.abs(event.clientY - startY) > MOVE_TOLERANCE_PX) {
      cancelHold();
    }
  }

  function onPointerUp(): void {
    cancelHold();
  }

  /**
   * Клик после показанной подсказки гасим: человек удерживал элемент, чтобы
   * прочитать подпись, а не чтобы оформить заказ.
   */
  function onClickCapture(event: MouseEvent): void {
    if (!shown) return;
    event.preventDefault();
    event.stopPropagation();
  }

  /** Системное меню по долгому нажатию перекрыло бы подсказку. */
  function onContextMenu(event: Event): void {
    if (shown || holdTimer) event.preventDefault();
  }

  function attach(): void {
    document.addEventListener("pointerdown", onPointerDown, { capture: true, passive: true });
    document.addEventListener("pointermove", onPointerMove, { capture: true, passive: true });
    document.addEventListener("pointercancel", onPointerUp, { capture: true, passive: true });
    document.addEventListener("pointerup", onPointerUp, { capture: true, passive: true });
    document.addEventListener("click", onClickCapture, true);
    document.addEventListener("contextmenu", onContextMenu, true);
    document.addEventListener("scroll", hide, { capture: true, passive: true });
  }

  function detach(): void {
    document.removeEventListener("pointerdown", onPointerDown, true);
    document.removeEventListener("pointermove", onPointerMove, true);
    document.removeEventListener("pointercancel", onPointerUp, true);
    document.removeEventListener("pointerup", onPointerUp, true);
    document.removeEventListener("click", onClickCapture, true);
    document.removeEventListener("contextmenu", onContextMenu, true);
    document.removeEventListener("scroll", hide, true);
    cancelHold();
    hide();
    hint?.remove();
    hint = null;
  }

  onMounted(() => {
    if (isTouch.value) attach();
  });

  // Планшет с подключённой мышью и без неё — один и тот же сеанс.
  watch(isTouch, (touch) => (touch ? attach() : detach()));

  onBeforeUnmount(detach);
}
